import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import axios from "../../../../api/axios";
import { fetchUsers, fetchUserAbsences } from "../../../../api/user";
import { fetchEvents as fetchCalendarEvents } from "../../../../api/events";
import {
  buildLinksPayload,
  addWorkOrderLink,
  addActivityLink,
  fetchProjectEventConflicts,
  fetchWorkOrderProjectEvents,
  pickPrimaryPlanForWorkOrder,
} from "../../../../api/projectPlan";
import ReportAgreementWeekCalendar from "../../service/reports/agreement-redesign/ReportAgreementWeekCalendar";
import "../../service/reports/agreement-redesign/report-agreement-redesign.css";
import PlanScheduleFormColumn from "./PlanScheduleFormColumn";
import PlanScheduleContextBar from "./PlanScheduleContextBar";
import PlanScheduleModalFoot, { PlanScheduleModalHead } from "./PlanScheduleModalFoot";
import {
  getEmploymentEndDate,
  isDateOnOrAfterEmploymentEnd,
} from "../../../utils/employmentEndDate";
import {
  addDays,
  absenceDaySet,
  applyHoursToDays,
  avatarColorForId,
  initials,
  availabilityFromEventCount,
  buildPlanResourcesPayload,
  buildWeekScheduleBlocks,
  formatWeekLabel,
  getDaySchedule,
  hasOverlap,
  hoursToDurationSlots,
  canonicalizePlanSelection,
  hydratePlanSelectionFromResources,
  mergeDaySchedules,
  planSelectionDateRange,
  planSelectionHasValidSchedule,
  planDayIndex,
  planDateRangeKeys,
  sortPlanDayKeys,
  startOfWeek,
  toDateString,
  TOTAL_SLOTS,
  weekRangeParams,
  wallHourToSlot,
  DEFAULT_WORK_START_HOUR,
  fmtNoShort,
  isPlanDateKey,
  slotToTime,
} from "../../service/reports/agreement-redesign/schedule-utils";

const activityDisplayTitle = (pa) =>
  pa?.display_title || pa?.displayTitle || pa?.title || "";

const defaultEstimatedHours = (workOrder, projectActivity) => {
  const bh = Number(workOrder?.budgeted_hours);
  if (Number.isFinite(bh) && bh > 0) return bh;
  const pah = Number(projectActivity?.budget_hours);
  if (Number.isFinite(pah) && pah > 0) return pah;
  return 8;
};

const defaultSlotRange = () => {
  const startSlot = wallHourToSlot(DEFAULT_WORK_START_HOUR);
  return {
    startSlot,
    endSlot: Math.min(startSlot + hoursToDurationSlots(8), TOTAL_SLOTS),
  };
};

const parsePlanWeekStart = (initialDate) => {
  if (!initialDate) return startOfWeek(new Date());
  const raw =
    typeof initialDate === "string"
      ? initialDate.includes("T")
        ? initialDate
        : `${initialDate}T12:00:00`
      : initialDate;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return startOfWeek(new Date());
  return startOfWeek(d);
};

const mergeParticipantEmployees = (fetched, participants) => {
  const byId = new Map();
  (participants || []).forEach((p) => {
    const u = p.user;
    if (!u?.id) return;
    byId.set(String(u.id), {
      id: u.id,
      name: u.name,
      displayTitle: u.displayTitle || u.name,
      email: u.email,
      __fromParticipant: true,
    });
  });
  (fetched || []).forEach((e) => {
    const key = String(e.id);
    if (!byId.has(key)) byId.set(key, e);
  });
  return Array.from(byId.values());
};

/** Tabs (Alle + each selected user) and the shared / per-user mode switch,
 *  rendered directly above the week calendar. */
function PlanUserTabs({
  employees,
  activeUserId,
  onSelectUser,
  planMode,
  onModeChange,
  planSelectionByUser,
  onApplyDaysToAll = null,
  t,
}) {
  return (
    <div className="ra-plan-tabs">
      <div className="ra-plan-tablist">
        <button
          type="button"
          className={`ra-plan-tab${activeUserId == null ? " active" : ""}`}
          onClick={() => onSelectUser(null)}
        >
          {t("work_order.plan_modal.tab_all")}
        </button>
        {employees.map((e) => {
          const hasOverride = Boolean(planSelectionByUser[e.id]);
          return (
            <button
              key={e.id}
              type="button"
              className={`ra-plan-tab${activeUserId === e.id ? " active" : ""}`}
              onClick={() => onSelectUser(e.id)}
              title={e.displayTitle || e.name}
            >
              <span
                className="ra-emp-avatar"
                style={{ background: avatarColorForId(e.id) }}
              >
                {initials(e.displayTitle || e.name)}
              </span>
              {e.displayTitle || e.name}
              {planMode === "per_user" && hasOverride ? (
                <span className="ra-plan-tab-dot" aria-hidden="true" />
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="ra-plan-modeswitch" role="group">
        <button
          type="button"
          className={planMode === "shared" ? "active" : ""}
          onClick={() => onModeChange("shared")}
        >
          {t("work_order.plan_modal.mode_shared")}
        </button>
        <button
          type="button"
          className={planMode === "per_user" ? "active" : ""}
          onClick={() => onModeChange("per_user")}
        >
          {t("work_order.plan_modal.mode_per_user")}
        </button>
        {onApplyDaysToAll ? (
          <button
            type="button"
            className="ra-plan-apply-days"
            onClick={onApplyDaysToAll}
            title={t("work_order.plan_modal.apply_days_to_all_help")}
          >
            {t("work_order.plan_modal.apply_days_to_all")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function PlanScheduleModal({
  open,
  onClose,
  workOrder = null,
  projectActivity = null,
  project = null,
  plan = null,
  initialDate = null,
  /** When set (e.g. draft dropped on a day), pre-select that day in the calendar. */
  preselectDate = null,
  schedulingMode = null,
  onEditProjectEvent = null,
  forceCreate = false,
  onSaved,
}) {
  const { t } = useTranslation();
  const projectId =
    project?.id ??
    workOrder?.project_id ??
    projectActivity?.project_id ??
    plan?.project_id;

  const effectiveSchedulingMode =
    schedulingMode ||
    (workOrder?.id ? "work_order" : projectActivity?.id ? "project_activity" : "project");

  const effectiveActivity =
    projectActivity ||
    plan?.project_activity ||
    null;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [estimatedHours, setEstimatedHours] = useState(8);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [planAnchorWeekStart, setPlanAnchorWeekStart] = useState(() =>
    startOfWeek(new Date())
  );
  const [planSelection, setPlanSelection] = useState({
    days: [],
    daySchedules: {},
    ...defaultSlotRange(),
  });
  // Plan-level days for "Kun dato" only. Kept separate from `planSelection`
  // so a drop day / saved start_date never auto-assigns resource days.
  const [datesOnlySelection, setDatesOnlySelection] = useState({
    days: [],
    daySchedules: {},
    ...defaultSlotRange(),
  });
  const [existingEvents, setExistingEvents] = useState([]);
  const [absenceDays, setAbsenceDays] = useState(() => new Set());
  const [eventsLoading, setEventsLoading] = useState(false);
  /** Conflicts from save API that block submit — clickable to jump to date. */
  const [blockedConflicts, setBlockedConflicts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [resolvedWorkOrderPlan, setResolvedWorkOrderPlan] = useState(null);

  // "resources" → assign executors with times per day.
  // "dates_only" → only set start/end dates now; resources can be added later.
  const [planningType, setPlanningType] = useState("resources");

  // "shared" → one plan applied to every selected user.
  // "per_user" → each user can have their own schedule (falls back to the
  // shared/template selection when they have no override yet).
  const [planMode, setPlanMode] = useState("shared");
  const [planSelectionByUser, setPlanSelectionByUser] = useState({});
  // null = the "Alle" tab (edits the shared/template selection).
  const [activeUserId, setActiveUserId] = useState(null);
  const scheduleInitKeyRef = useRef(null);

  // When creating an additional plan for a work order, don't fall back to the
  // existing primary plan — start from a blank plan instead.
  const effectivePlan = plan ?? (forceCreate ? null : resolvedWorkOrderPlan);

  const isDatesOnly = planningType === "dates_only";

  const workOrderLabel = useMemo(() => {
    if (!workOrder?.id) return null;
    return (
      workOrder.displayTitle ||
      (workOrder.number ? `#${workOrder.number}` : null) ||
      workOrder.title
    );
  }, [workOrder]);

  const projectActivityLabel = useMemo(() => {
    if (!effectiveActivity?.id) return null;
    return activityDisplayTitle(effectiveActivity) || `#${effectiveActivity.id}`;
  }, [effectiveActivity]);

  const isSimpleActivityMode =
    effectiveSchedulingMode === "project_activity" && !workOrder?.id;

  const showContextBar =
    !isSimpleActivityMode || Boolean(effectivePlan?.id) || Boolean(onEditProjectEvent);

  const durationSlots = useMemo(
    () => hoursToDurationSlots(estimatedHours),
    [estimatedHours]
  );

  useEffect(() => {
    if (!open) return;
    document.documentElement.style.setProperty("--slot-h", "14px");
  }, [open]);

  // Hydrate form/calendar once per open + plan context. Must not re-run when the
  // user navigates weeks in the calendar (weekStart is independent state).
  useEffect(() => {
    if (!open) {
      scheduleInitKeyRef.current = null;
      return;
    }

    const initKey = [
      plan?.id ?? "no-plan",
      forceCreate ? "force" : "",
      workOrder?.id ?? "",
      effectiveActivity?.id ?? "",
      initialDate ?? "",
      preselectDate ?? "",
      effectivePlan?.id ?? "no-effective",
      effectivePlan?.resources?.length ?? 0,
      effectivePlan?.start_date ?? "",
      effectivePlan?.end_date ?? "",
    ].join("|");

    if (scheduleInitKeyRef.current === initKey) return;
    scheduleInitKeyRef.current = initKey;

    setPlanMode("shared");
    setPlanSelectionByUser({});
    setActiveUserId(null);
    setBlockedConflicts([]);
    // Dates-only when the plan already has dates without crew, OR when the
    // user dropped an existing draft onto a day (intent: set a plan-level
    // date). Day-picker "New plan" also passes preselectDate as a week
    // anchor, but has no draft — keep that on resource planning with empty
    // resource days (planner picks them manually).
    const droppedOntoDay =
      Boolean(preselectDate) &&
      Boolean(effectivePlan?.id) &&
      !effectivePlan?.resources?.length;
    const existingDatesOnly =
      effectivePlan?.id &&
      effectivePlan.start_date &&
      !effectivePlan.resources?.length;
    setPlanningType(
      droppedOntoDay || existingDatesOnly ? "dates_only" : "resources"
    );
    setSearch("");
    // New additional plan (forceCreate): start with empty title so the user can
    // name rounds (e.g. Forberedelse / Montasje) instead of copying the WO title.
    if (forceCreate) {
      setTitle("");
      setDescription("");
    } else {
      setTitle(
        plan?.title ||
          effectivePlan?.title ||
          workOrder?.title ||
          workOrder?.displayTitle ||
          activityDisplayTitle(effectiveActivity) ||
          ""
      );
      setDescription(
        plan?.description ||
          effectivePlan?.description ||
          workOrder?.description ||
          effectiveActivity?.description ||
          ""
      );
    }

    if (effectivePlan?.resources?.length) {
      const hydrated = hydratePlanSelectionFromResources(effectivePlan.resources);
      setWeekStart(hydrated.weekStart);
      setPlanAnchorWeekStart(hydrated.weekStart);
      setSelectedEmployeeIds(hydrated.employeeIds);
      setPlanSelection(hydrated.selection);
      setPlanSelectionByUser(hydrated.selectionByUser || {});
      setPlanMode(hydrated.planMode || "shared");
      setActiveUserId(
        hydrated.planMode === "per_user" && hydrated.employeeIds?.length
          ? hydrated.employeeIds[0]
          : null
      );
      setDatesOnlySelection({
        days: [],
        daySchedules: {},
        ...defaultSlotRange(),
      });
      setEstimatedHours(hydrated.estimatedHours);
    } else {
      setEstimatedHours(defaultEstimatedHours(workOrder, effectiveActivity));
      setSelectedEmployeeIds([]);
      const slots = defaultSlotRange();
      const savedRangeDays =
        effectivePlan?.start_date && !effectivePlan?.resources?.length
          ? planDateRangeKeys(
              effectivePlan.start_date.substring(0, 10),
              (
                effectivePlan.end_date || effectivePlan.start_date
              ).substring(0, 10)
            )
          : [];
      // Drop day / saved range apply only to plan-level "Kun dato" selection.
      // Resource shared days always start empty so employees are never
      // auto-assigned the plan from-date.
      const dropDayKey = (() => {
        if (!preselectDate) return null;
        if (typeof preselectDate === "string") {
          return preselectDate.substring(0, 10);
        }
        try {
          return toDateString(new Date(preselectDate));
        } catch {
          return null;
        }
      })();
      // Only pre-select a day when the user dropped an existing draft onto it.
      // New plans must leave days empty — auto-selecting today created wrong plans.
      const applyDropDay = Boolean(dropDayKey) && Boolean(effectivePlan?.id);
      const planLevelDays = applyDropDay
        ? [dropDayKey]
        : savedRangeDays.length
          ? savedRangeDays
          : [];
      const planLevelSchedules = {};
      planLevelDays.forEach((dayKey) => {
        planLevelSchedules[dayKey] = {
          startSlot: slots.startSlot,
          endSlot: slots.endSlot,
        };
      });
      const anchor = dropDayKey
        ? parsePlanWeekStart(dropDayKey)
        : effectivePlan?.start_date
          ? parsePlanWeekStart(effectivePlan.start_date.substring(0, 10))
          : parsePlanWeekStart(initialDate);
      setDatesOnlySelection(
        canonicalizePlanSelection(
          { days: planLevelDays, daySchedules: planLevelSchedules, ...slots },
          anchor
        )
      );
      setPlanSelection(
        canonicalizePlanSelection(
          { days: [], daySchedules: {}, ...slots },
          anchor
        )
      );
      setPlanAnchorWeekStart(anchor);
      setWeekStart(anchor);
    }
  }, [
    open,
    plan?.id,
    plan?.title,
    plan?.description,
    forceCreate,
    workOrder?.id,
    workOrder?.title,
    workOrder?.displayTitle,
    effectiveActivity?.id,
    initialDate,
    preselectDate,
    effectivePlan?.id,
    effectivePlan?.title,
    effectivePlan?.description,
    effectivePlan?.resources?.length,
    effectivePlan?.start_date,
    effectivePlan?.end_date,
  ]);

  useEffect(() => {
    if (!open || !workOrder?.id || plan?.id || forceCreate) {
      setResolvedWorkOrderPlan(null);
      return;
    }
    let cancelled = false;
    fetchWorkOrderProjectEvents(workOrder.id, {
      fields:
        "id,title,description,status,start_date,end_date,project_id,work_order_id,project_activity_id,links,resources,resources.user",
    })
      .then((res) => {
        if (cancelled) return;
        setResolvedWorkOrderPlan(
          pickPrimaryPlanForWorkOrder(res.data?.data || [])
        );
      })
      .catch(() => {
        if (!cancelled) setResolvedWorkOrderPlan(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workOrder?.id, plan?.id, forceCreate]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEmployeesLoading(true);
    fetchUsers({
      page: 1,
      per_page: 80,
      sortBy: "name",
      sort: "asc",
      fields: "id,name,displayTitle,email,user_type_id,employment.employment_ends,employment.end_date",
      filters: [{ name: "EMPLOYEE_OR_HIRED", value: true }],
      ...(search.trim() ? { query: search.trim() } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const merged = mergeParticipantEmployees(list, workOrder?.participants);
        setEmployees(merged);
        // Do not auto-pick executors for a brand-new plan — the user must
        // explicitly choose who to schedule (or switch to "Kun dato").
      })
      .catch(() => {
        if (!cancelled) setEmployees([]);
      })
      .finally(() => {
        if (!cancelled) setEmployeesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, search, workOrder?.id, workOrder?.participants, effectivePlan?.resources?.length]);

  // Keep the active calendar tab valid as the selection changes.
  useEffect(() => {
    if (activeUserId != null && !selectedEmployeeIds.includes(Number(activeUserId))) {
      setActiveUserId(null);
    }
  }, [activeUserId, selectedEmployeeIds]);

  // Calendar context follows the active tab: a specific user shows only their
  // events/absence; the "Alle" tab shows the union for all selected users.
  const calendarUserIds = useMemo(
    () =>
      isDatesOnly
        ? []
        : activeUserId != null &&
            selectedEmployeeIds.includes(Number(activeUserId))
          ? [Number(activeUserId)]
          : selectedEmployeeIds,
    [isDatesOnly, activeUserId, selectedEmployeeIds]
  );

  useEffect(() => {
    if (!open || calendarUserIds.length === 0) {
      setExistingEvents([]);
      setAbsenceDays(new Set());
      return;
    }
    let cancelled = false;
    setEventsLoading(true);
    const range = weekRangeParams(weekStart);
    Promise.all([
      fetchCalendarEvents({
        date: toDateString(weekStart),
        view: "week",
        users: calendarUserIds.map(String).join(","),
      }),
      Promise.all(
        calendarUserIds.map((uid) => fetchUserAbsences(uid, range))
      ),
    ])
      .then(([eventsRes, absenceResponses]) => {
        if (cancelled) return;
        const items = Array.isArray(eventsRes.data) ? eventsRes.data : [];
        // Exclude this plan's own calendar rows so editing/moving does not
        // self-conflict (and stale orphans for this plan stay hidden).
        // GET /events uses module "project_event" (related_type); main calendar
        // / module filters also use "project_plan" with module_id = plan id.
        const planId = effectivePlan?.id != null ? Number(effectivePlan.id) : null;
        const isOwnPlanEvent = (ev) => {
          if (planId == null || Number(ev?.module_id) !== planId) return false;
          return (
            ev?.module === "project_event" || ev?.module === "project_plan"
          );
        };
        const otherItems =
          planId == null ? items : items.filter((ev) => !isOwnPlanEvent(ev));
        const mergedAbsences = absenceResponses.flatMap((res) =>
          Array.isArray(res?.data) ? res.data : []
        );
        setAbsenceDays(absenceDaySet(mergedAbsences, weekStart));
        setExistingEvents(
          buildWeekScheduleBlocks(
            otherItems,
            mergedAbsences,
            weekStart,
            t("absence")
          )
        );
      })
      .catch(() => {
        if (!cancelled) {
          setExistingEvents([]);
          setAbsenceDays(new Set());
        }
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, calendarUserIds, weekStart, t, effectivePlan?.id]);

  const visibleEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.displayTitle || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q)
    );
  }, [employees, search]);

  const selectedEmployees = useMemo(
    () => employees.filter((e) => selectedEmployeeIds.includes(e.id)),
    [employees, selectedEmployeeIds]
  );

  const handleWeekChange = useCallback((direction) => {
    setWeekStart((prev) => addDays(prev, direction * 7));
  }, []);

  const handleGoTodayWeek = useCallback(() => {
    setWeekStart(startOfWeek(new Date()));
  }, []);

  const weekEnd = addDays(weekStart, 6);
  const weekLabel = formatWeekLabel(weekStart, weekEnd);

  // Active selection = the schedule the calendar/form currently edit.
  // Dates-only uses a separate plan-level selection so drop/from-date never
  // leaks into resource day assignments. In resource mode, shared/per-user
  // templates apply as before.
  const activeSelection = isDatesOnly
    ? datesOnlySelection
    : planMode === "per_user" && activeUserId != null
      ? planSelectionByUser[activeUserId] ?? planSelection
      : planSelection;

  // The schedule a given user will actually be saved with.
  const selectionForUser = useCallback(
    (userId) =>
      planMode === "per_user"
        ? planSelectionByUser[userId] ?? planSelection
        : planSelection,
    [planMode, planSelectionByUser, planSelection]
  );

  const hasValidSchedule = planSelectionHasValidSchedule(activeSelection);

  // Submit requires a complete schedule for every selected executor in
  // per-user mode (otherwise save would silently drop people without days).
  // Dates-only plans just need at least one selected plan-level day.
  const canSubmitSchedule = isDatesOnly
    ? planSelectionHasValidSchedule(datesOnlySelection)
    : planMode === "per_user"
      ? selectedEmployeeIds.length > 0 &&
        selectedEmployeeIds.every((uid) =>
          planSelectionHasValidSchedule(selectionForUser(uid))
        )
      : planSelectionHasValidSchedule(planSelection) &&
        selectedEmployeeIds.length > 0;

  const assignmentCount = isDatesOnly
    ? 0
    : planMode === "per_user"
      ? selectedEmployeeIds.reduce((sum, uid) => {
          const sel = selectionForUser(uid);
          return (
            sum + (planSelectionHasValidSchedule(sel) ? sel.days?.length || 0 : 0)
          );
        }, 0)
      : hasValidSchedule
        ? (planSelection.days?.length || 0) * selectedEmployeeIds.length
        : 0;

  const planConflicts = useMemo(() => {
    if (!hasValidSchedule) return [];
    const items = [];
    (activeSelection.days || []).forEach((dayKey) => {
      const dayIdx = planDayIndex(weekStart, dayKey);
      if (dayIdx == null) return;
      const daySlots = getDaySchedule(activeSelection, dayKey);
      if (!daySlots) return;
      const apt = { day: dayIdx, ...daySlots };
      existingEvents.forEach((ev) => {
        if (ev.day !== dayIdx || !hasOverlap(ev, apt)) return;
        const dateStr = isPlanDateKey(dayKey)
          ? dayKey
          : toDateString(addDays(weekStart, dayKey));
        items.push({
          key: `live-${dateStr}-${ev.id || ev.title}-${ev.startSlot}`,
          dayKey: dateStr,
          title: ev.title || t("Conflict"),
          timeLabel: `${slotToTime(ev.startSlot)}–${slotToTime(ev.endSlot)}`,
          source: "calendar",
        });
      });
    });
    return items;
  }, [hasValidSchedule, activeSelection, existingEvents, weekStart, t]);

  const conflictItems = useMemo(() => {
    const seen = new Set();
    const merged = [];
    [...planConflicts, ...blockedConflicts].forEach((c) => {
      const key = c.key || `${c.dayKey}-${c.title}-${c.timeLabel}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push({ ...c, key });
    });
    return merged.sort((a, b) =>
      String(a.dayKey || "").localeCompare(String(b.dayKey || ""))
    );
  }, [planConflicts, blockedConflicts]);

  const jumpToConflictDay = useCallback((dayKey, userId) => {
    if (!dayKey) return;
    const anchor = parsePlanWeekStart(dayKey);
    setWeekStart(anchor);
    // Prefer an explicit userId (save-time path sets state in the same turn,
    // so blockedConflicts may still be stale). Fall back to state for list clicks.
    const uid =
      userId != null
        ? Number(userId)
        : blockedConflicts.find((c) => c.dayKey === dayKey)?.userId;
    if (uid != null && selectedEmployeeIds.includes(Number(uid))) {
      setPlanMode("per_user");
      setActiveUserId(Number(uid));
    }
  }, [blockedConflicts, selectedEmployeeIds]);

  // Drop stale save-time conflicts once crew or *any* scheduled days change
  // (not when merely switching the active user tab / jumping to a conflict).
  const allScheduledDaysKey = useMemo(() => {
    if (planMode === "per_user") {
      const days = new Set();
      selectedEmployeeIds.forEach((uid) => {
        (selectionForUser(uid)?.days || []).forEach((d) => days.add(d));
      });
      return [...days].sort(sortPlanDayKeys).join(",");
    }
    return [...(planSelection.days || [])].sort(sortPlanDayKeys).join(",");
  }, [planMode, selectedEmployeeIds, selectionForUser, planSelection.days]);
  const selectedIdsKey = selectedEmployeeIds.join(",");
  useEffect(() => {
    setBlockedConflicts([]);
  }, [allScheduledDaysKey, selectedIdsKey]);

  // Apply a new employee selection while protecting already-scheduled people.
  //
  // Default (single toggle): anyone newly added when the plan already has days
  // is seeded with an EMPTY per-user schedule and the modal switches to
  // per-user mode so they are not copied onto every existing day. Existing
  // assignees keep their current schedule as an explicit override.
  //
  // Bulk select ({ inheritShared: true }): keep "Same plan for all" — newly
  // selected people inherit the shared day template. Use this for
  // "select all participants/employees" so many resources share the same days.
  const applySelectionWithDaySafety = useCallback(
    (nextIds, { inheritShared = false } = {}) => {
      const prevIds = selectedEmployeeIds;
      const nextSet = new Set(nextIds);
      const addedIds = nextIds.filter((id) => !prevIds.includes(id));
      // Only protect *existing* assignees: the first employee(s) on a plan
      // should still inherit the shared day selection the user already picked.
      // Extra people added later start blank so they are not copied onto every
      // previously scheduled day — unless the caller asked to inherit shared.
      const existingAssigneesHaveDays =
        prevIds.length > 0 &&
        (planMode === "per_user"
          ? prevIds.some((uid) => (selectionForUser(uid)?.days || []).length > 0)
          : (planSelection.days || []).length > 0);
      const shouldSeedPerUser =
        !inheritShared && addedIds.length > 0 && existingAssigneesHaveDays;

      if (inheritShared) {
        // Bulk add: shared template for everyone — new people get the same days.
        setPlanSelectionByUser({});
        setPlanMode("shared");
        setActiveUserId(null);
        setSelectedEmployeeIds(nextIds);
        return;
      }

      setPlanSelectionByUser((prev) => {
        const next = {};
        Object.keys(prev).forEach((uidKey) => {
          if (nextSet.has(Number(uidKey))) next[uidKey] = prev[uidKey];
        });
        if (shouldSeedPerUser) {
          prevIds.forEach((uid) => {
            if (next[uid] === undefined) next[uid] = selectionForUser(uid);
          });
          addedIds.forEach((uid) => {
            next[uid] = { days: [], daySchedules: {}, ...defaultSlotRange() };
          });
        }
        return next;
      });

      if (shouldSeedPerUser) {
        setPlanMode("per_user");
        setActiveUserId(addedIds[0]);
        toast(
          addedIds.length === 1
            ? t("work_order.plan_modal.new_resource_pick_days")
            : t("work_order.plan_modal.new_resources_pick_days")
        );
      }

      setSelectedEmployeeIds(nextIds);
    },
    [selectedEmployeeIds, planMode, planSelection, selectionForUser, t]
  );

  const toggleEmployee = useCallback(
    (id) => {
      const key = Number(id);
      const nextIds = selectedEmployeeIds.includes(key)
        ? selectedEmployeeIds.filter((x) => x !== key)
        : [...selectedEmployeeIds, key];
      applySelectionWithDaySafety(nextIds);
    },
    [selectedEmployeeIds, applySelectionWithDaySafety]
  );

  const selectAllParticipants = useCallback(() => {
    const ids = employees
      .filter((e) => e.__fromParticipant)
      .map((e) => Number(e.id));
    if (ids.length) applySelectionWithDaySafety(ids, { inheritShared: true });
  }, [employees, applySelectionWithDaySafety]);

  const selectAllEmployees = useCallback(() => {
    const ids = employees.map((e) => Number(e.id));
    if (ids.length) applySelectionWithDaySafety(ids, { inheritShared: true });
  }, [employees, applySelectionWithDaySafety]);

  const clearEmployees = useCallback(() => {
    applySelectionWithDaySafety([]);
  }, [applySelectionWithDaySafety]);

  // Promote the currently visible day selection to the shared template and
  // apply it to every selected resource ("Same plan for all").
  const applyActiveDaysToAll = useCallback(() => {
    const source = activeSelection;
    if (!planSelectionHasValidSchedule(source)) {
      toast.error(t("work_order.plan_modal.pick_days_hint"));
      return;
    }
    setPlanSelection(
      canonicalizePlanSelection(source, planAnchorWeekStart || weekStart)
    );
    setPlanSelectionByUser({});
    setPlanMode("shared");
    setActiveUserId(null);
    toast.success(t("work_order.plan_modal.apply_days_to_all_toast"));
  }, [activeSelection, planAnchorWeekStart, weekStart, t]);

  // Mode toggle only changes which schedule is edited/saved. Per-user
  // overrides stay in memory so an accidental switch back to "Same plan for
  // all" does not wipe them — use "Apply selected days to all" to promote.
  const handlePlanModeChange = useCallback((mode) => {
    setPlanMode(mode);
    if (mode === "shared") {
      setActiveUserId(null);
    }
  }, []);

  const computeNextSelection = useCallback(
    (prev, next) => {
      if (next.removeDay != null) {
        const days = (prev.days || []).filter((d) => d !== next.removeDay);
        const daySchedules = { ...(prev.daySchedules || {}) };
        delete daySchedules[next.removeDay];
        const first = days[0];
        const firstSlots = first != null ? daySchedules[first] : null;
        return {
          ...prev,
          days,
          daySchedules,
          ...(firstSlots
            ? { startSlot: firstSlots.startSlot, endSlot: firstSlots.endSlot }
            : {}),
        };
      }
      if (next.calendarDayPatch) {
        const { day, startSlot, endSlot } = next.calendarDayPatch;
        const days = prev.days.includes(day)
          ? prev.days
          : [...prev.days, day].sort(sortPlanDayKeys);
        const daySchedules = {
          ...(prev.daySchedules || {}),
          [day]: { startSlot, endSlot },
        };
        const first = days[0];
        const firstSlots = daySchedules[first];
        return {
          ...prev,
          days,
          daySchedules,
          ...(firstSlots
            ? { startSlot: firstSlots.startSlot, endSlot: firstSlots.endSlot }
            : {}),
        };
      }
      if (next.calendarRangePatch) {
        const { startDay, endDay, startSlot, endSlot } = next.calendarRangePatch;
        const rangeDays = planDateRangeKeys(startDay, endDay);
        const days = [...new Set([...prev.days, ...rangeDays])].sort(
          sortPlanDayKeys
        );
        const daySchedules = mergeDaySchedules(prev, rangeDays, {
          startSlot,
          endSlot,
        });
        const firstSlots = daySchedules[days[0]];
        return {
          ...prev,
          days,
          daySchedules,
          ...(firstSlots
            ? { startSlot: firstSlots.startSlot, endSlot: firstSlots.endSlot }
            : {}),
        };
      }
      const merged = { ...prev };
      if (next.days !== undefined) merged.days = next.days;
      if (next.startSlot !== undefined) merged.startSlot = next.startSlot;
      if (next.endSlot !== undefined) merged.endSlot = next.endSlot;
      if (next.daySchedules) {
        merged.daySchedules = {
          ...(prev.daySchedules || {}),
          ...next.daySchedules,
        };
      }
      if (merged.startSlot != null && merged.endSlot == null) {
        merged.endSlot = Math.min(merged.startSlot + durationSlots, TOTAL_SLOTS);
      }
      if (merged.daySchedules && merged.days?.length) {
        const patched = { ...merged.daySchedules };
        merged.days.forEach((day) => {
          const slots = getDaySchedule(merged, day);
          if (slots) patched[day] = slots;
        });
        merged.daySchedules = patched;
      }
      return canonicalizePlanSelection(merged, planAnchorWeekStart);
    },
    [durationSlots, planAnchorWeekStart]
  );

  // Route a selection update to dates-only, shared template, or user override.
  const mutateActiveSelection = useCallback(
    (updater) => {
      if (isDatesOnly) {
        setDatesOnlySelection((prev) => updater(prev));
        return;
      }
      if (planMode === "shared" || activeUserId == null) {
        setPlanSelection((prev) => updater(prev));
        return;
      }
      setPlanSelectionByUser((prev) => {
        const base =
          prev[activeUserId] ??
          planSelection ?? { days: [], daySchedules: {}, ...defaultSlotRange() };
        return { ...prev, [activeUserId]: updater(base) };
      });
    },
    [isDatesOnly, planMode, activeUserId, planSelection]
  );

  // Carry the active calendar selection into the newly selected planning type
  // when that target is still empty, so toggling modes does not blank the
  // week. Init still keeps drop/plan dates out of resource selection until the
  // user either picks days or explicitly switches here.
  const handlePlanningTypeChange = useCallback(
    (nextType) => {
      if (nextType === planningType) return;
      const anchor = planAnchorWeekStart || weekStart;
      if (nextType === "dates_only") {
        if (!datesOnlySelection?.days?.length && planSelection?.days?.length) {
          setDatesOnlySelection(
            canonicalizePlanSelection({ ...planSelection }, anchor)
          );
        }
      } else if (nextType === "resources") {
        if (!planSelection?.days?.length && datesOnlySelection?.days?.length) {
          setPlanSelection(
            canonicalizePlanSelection({ ...datesOnlySelection }, anchor)
          );
        }
      }
      setPlanningType(nextType);
    },
    [
      planningType,
      planSelection,
      datesOnlySelection,
      planAnchorWeekStart,
      weekStart,
    ]
  );

  const onActiveSelectionChange = useCallback(
    (next) => {
      mutateActiveSelection((prev) => computeNextSelection(prev, next));
    },
    [mutateActiveSelection, computeNextSelection]
  );

  const removeActivePlanDay = useCallback(
    (dayKey) => {
      onActiveSelectionChange({ removeDay: dayKey });
    },
    [onActiveSelectionChange]
  );

  const handleEstimatedHoursChange = useCallback(
    (hours) => {
      setEstimatedHours(hours);
      mutateActiveSelection((prev) => {
        if (!prev.days?.length) return prev;
        return applyHoursToDays(prev, hours);
      });
    },
    [mutateActiveSelection]
  );

  const handleApplyEstimatedHoursToAllDays = useCallback(() => {
    mutateActiveSelection((prev) => applyHoursToDays(prev, estimatedHours));
  }, [mutateActiveSelection, estimatedHours]);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    if (
      !canSubmitSchedule ||
      (!isDatesOnly && selectedEmployeeIds.length === 0) ||
      !projectId
    ) {
      toast.error(
        isDatesOnly
          ? t("work_order.plan_modal.validation_missing_dates")
          : t("work_order.plan_modal.validation_missing")
      );
      return;
    }
    // When the title field is shown (work-order / project plan), require an
    // explicit plantittel so rounds like «Forberedelse» / «Montasje» stay distinct.
    const planTitle = (
      title ||
      (isSimpleActivityMode
        ? activityDisplayTitle(effectiveActivity) ||
          workOrder?.title ||
          workOrder?.displayTitle ||
          ""
        : "")
    ).trim();
    if (!planTitle) {
      toast.error(t("project.event.title_required"));
      return;
    }

    // Show loading on the Save button immediately — conflict checks and the
    // save request can take a while, and users need instant feedback.
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));

    try {
      let resources = [];
      if (isDatesOnly) {
        // Dates-only plan: no resources are attached (and any existing ones
        // are removed when explicitly switching an existing plan to dates-only).
      } else if (planMode === "per_user") {
        const missingDays = selectedEmployeeIds.filter(
          (uid) => !planSelectionHasValidSchedule(selectionForUser(uid))
        );
        if (missingDays.length) {
          setActiveUserId(missingDays[0]);
          toast.error(t("work_order.plan_modal.validation_missing_user_days"));
          return;
        }
        // Each user is saved with their own override (or the shared template).
        selectedEmployeeIds.forEach((uid) => {
          const sel = selectionForUser(uid);
          resources.push(
            ...buildPlanResourcesPayload(
              sel,
              weekStart,
              [uid],
              effectivePlan?.resources || []
            )
          );
        });
      } else {
        resources = buildPlanResourcesPayload(
          planSelection,
          weekStart,
          selectedEmployeeIds,
          effectivePlan?.resources || []
        );
      }
      if (!isDatesOnly && !resources.length) {
        toast.error(t("work_order.plan_modal.validation_missing"));
        return;
      }

      const endedResource = (resources || []).find((resource) => {
        const emp = employees.find((e) => Number(e.id) === Number(resource.user_id));
        return isDateOnOrAfterEmploymentEnd(resource.start_time, emp);
      });
      if (endedResource) {
        const emp = employees.find(
          (e) => Number(e.id) === Number(endedResource.user_id)
        );
        toast.error(
          t("employment.end_date.plan_blocked", {
            name: emp?.displayTitle || emp?.name || "",
            date: getEmploymentEndDate(emp) || "",
          })
        );
        return;
      }

      const saveBlocked = [];
      for (const resource of resources) {
        try {
          const conflictRes = await fetchProjectEventConflicts({
            user_ids: resource.user_id,
            start_time: new Date(resource.start_time).toISOString(),
            end_time: new Date(resource.end_time).toISOString(),
            exclude_resource_id: resource.id,
            ...(effectivePlan?.id
              ? { exclude_project_event_id: effectivePlan.id }
              : {}),
          });
          const planHits = conflictRes.data?.plan || [];
          const calHits = conflictRes.data?.calendar || [];
          if (planHits.length + calHits.length > 0) {
            const dayKey = String(resource.start_time).substring(0, 10);
            const emp =
              employees.find((e) => Number(e.id) === Number(resource.user_id)) ||
              null;
            const empName =
              emp?.displayTitle || emp?.name || `#${resource.user_id}`;
            [...planHits, ...calHits].forEach((hit, idx) => {
              const title =
                hit?.title ||
                hit?.displayTitle ||
                t("project.event.conflict_warning");
              const timeLabel = hit?.start_time
                ? `${String(hit.start_time).substring(11, 16)}–${String(
                    hit.end_time || ""
                  ).substring(11, 16)}`
                : "";
              saveBlocked.push({
                key: `save-${resource.user_id}-${dayKey}-${idx}-${title}`,
                dayKey,
                userId: Number(resource.user_id),
                title: `${empName}: ${title}`,
                timeLabel,
                source: "save",
              });
            });
          }
        } catch {
          /* continue */
        }
      }
      if (saveBlocked.length > 0) {
        setBlockedConflicts(saveBlocked);
        jumpToConflictDay(saveBlocked[0].dayKey, saveBlocked[0].userId);
        toast.error(t("project.event.conflict_warning"));
        return;
      }
      setBlockedConflicts([]);

      let dateRange;
      if (isDatesOnly) {
        dateRange = planSelectionDateRange(datesOnlySelection);
      } else if (planMode === "per_user") {
        const combinedDays = new Set();
        selectedEmployeeIds.forEach((uid) => {
          const sel = selectionForUser(uid);
          if (!planSelectionHasValidSchedule(sel)) return;
          (sel?.days || []).forEach((d) => combinedDays.add(d));
        });
        dateRange = planSelectionDateRange({ days: [...combinedDays] }, weekStart);
      } else {
        dateRange = planSelectionDateRange(planSelection);
      }
      const { start_date, end_date } = dateRange;

      let woLinks = [];
      let paLinks = [];
      if (workOrder?.id) {
        woLinks = addWorkOrderLink([], workOrder);
      }
      const paRecord =
        effectiveActivity ||
        (workOrder?.project_activity_id
          ? workOrder.project_activity || { id: workOrder.project_activity_id }
          : null);
      if (paRecord?.id) {
        paLinks = addActivityLink([], paRecord);
      }

      const payload = {
        project_event: {
          title: planTitle,
          description: (description || "").trim(),
          status: "scheduled",
          project_id: projectId,
          start_date,
          end_date,
          work_order_id: workOrder?.id || effectivePlan?.work_order_id || null,
          project_activity_id:
            paRecord?.id ||
            workOrder?.project_activity_id ||
            effectivePlan?.project_activity_id ||
            null,
          links: buildLinksPayload(woLinks, paLinks),
          resources,
        },
      };

      const existingPlanId = effectivePlan?.id;
      const url = existingPlanId
        ? `/project_events/${existingPlanId}`
        : "/project_events";
      const method = existingPlanId ? "put" : "post";
      const res = await axios({ method, url, data: payload });
      toast.success(
        existingPlanId
          ? isSimpleActivityMode
            ? t("project.plan.schedule_activity.toast_updated")
            : t("work_order.plan_modal.toast_updated")
          : isSimpleActivityMode
            ? t("project.plan.schedule_activity.toast_created")
            : t("work_order.plan_modal.toast_created")
      );
      onSaved?.(res.data);
      onClose();
    } catch (err) {
      const msgs = err.response?.data?.errors;
      if (Array.isArray(msgs)) toast.error(msgs.join(", "));
      else toast.error(t("Something went wrong"));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const modalTitle = (() => {
    if (effectiveSchedulingMode === "project_activity" && !workOrder?.id) {
      return effectivePlan?.id
        ? t("project.plan.schedule.activity_title_edit")
        : t("project.plan.schedule.activity_title_create");
    }
    if (effectiveSchedulingMode === "project" && !workOrder?.id) {
      return effectivePlan?.id
        ? t("project.plan.schedule.title_edit")
        : t("project.plan.schedule.title_create");
    }
    return effectivePlan?.id
      ? t("work_order.plan_modal.title_edit")
      : t("work_order.plan_modal.title_create");
  })();

  // On a user tab the calendar shows that person; on "Alle" no single employee
  // is highlighted (events shown are the union for all selected users).
  const calendarEmployee =
    activeUserId != null
      ? employees.find((e) => e.id === activeUserId) || null
      : null;

  const showUserTabs = !isDatesOnly && selectedEmployees.length > 0;
  const showApplyDaysToAll =
    showUserTabs &&
    selectedEmployeeIds.length > 1 &&
    planSelectionHasValidSchedule(activeSelection);
  const calendarTopBar = showUserTabs ? (
    <PlanUserTabs
      employees={selectedEmployees}
      activeUserId={activeUserId}
      onSelectUser={setActiveUserId}
      planMode={planMode}
      onModeChange={handlePlanModeChange}
      planSelectionByUser={planSelectionByUser}
      onApplyDaysToAll={showApplyDaysToAll ? applyActiveDaysToAll : null}
      t={t}
    />
  ) : null;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 flex min-h-full items-center justify-center overflow-y-auto p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:scale-95"
          >
            <Dialog.Panel className="report-agreement-redesign mx-auto w-full max-w-[1400px] transform text-left align-middle transition-all">
              <div className="ra-modal">
                <PlanScheduleModalHead
                  title={modalTitle}
                  subtitle={
                    workOrderLabel
                      ? workOrderLabel
                      : effectiveActivity?.display_title ||
                        effectiveActivity?.title ||
                        project?.displayTitle ||
                        project?.name
                  }
                  onClose={onClose}
                  t={t}
                />

                {showContextBar ? (
                  <PlanScheduleContextBar
                    projectId={projectId}
                    workOrder={workOrder}
                    projectActivity={effectiveActivity}
                    schedulingMode={effectiveSchedulingMode}
                    onEditProjectEvent={
                      effectivePlan?.id ? onEditProjectEvent : null
                    }
                  />
                ) : null}

                <PlanScheduleFormColumn
                  title={title}
                  onTitleChange={setTitle}
                  showTitleField={!isSimpleActivityMode}
                  description={description}
                  onDescriptionChange={setDescription}
                  showDescriptionField={!isSimpleActivityMode}
                  workOrderLabel={workOrderLabel}
                  projectActivityLabel={projectActivityLabel}
                  schedulingMode={effectiveSchedulingMode}
                  simpleActivityMode={isSimpleActivityMode}
                  planningType={planningType}
                  onPlanningTypeChange={handlePlanningTypeChange}
                  hasExistingResources={Boolean(effectivePlan?.resources?.length)}
                  employees={visibleEmployees}
                  selectedEmployeeIds={selectedEmployeeIds}
                  onToggleEmployee={toggleEmployee}
                  onSelectAllParticipants={
                    workOrder?.participants?.length ? selectAllParticipants : null
                  }
                  onSelectAllEmployees={
                    employees.length ? selectAllEmployees : null
                  }
                  onClearEmployees={
                    selectedEmployeeIds.length ? clearEmployees : null
                  }
                  search={search}
                  setSearch={setSearch}
                  estimatedHours={estimatedHours}
                  onEstimatedHoursChange={handleEstimatedHoursChange}
                  onApplyEstimatedHoursToAllDays={handleApplyEstimatedHoursToAllDays}
                  budgetedHoursHint={
                    workOrder?.budgeted_hours > 0
                      ? Number(workOrder.budgeted_hours)
                      : effectiveActivity?.budget_hours > 0
                        ? Number(effectiveActivity.budget_hours)
                        : null
                  }
                  planSelection={activeSelection}
                  onPlanSelectionChange={onActiveSelectionChange}
                  weekStart={weekStart}
                  weekLabel={weekLabel}
                  onWeekChange={handleWeekChange}
                  onGoToday={handleGoTodayWeek}
                />

                {!isDatesOnly && conflictItems.length > 0 ? (
                  <div className="mx-3 mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                      {t("work_order.plan_modal.conflicts_title", {
                        count: conflictItems.length,
                      })}
                    </div>
                    <p className="mt-0.5 text-xs text-amber-800/90">
                      {t("work_order.plan_modal.conflicts_help")}
                    </p>
                    <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto">
                      {conflictItems.map((c) => (
                        <li key={c.key}>
                          <button
                            type="button"
                            onClick={() => jumpToConflictDay(c.dayKey, c.userId)}
                            className="flex w-full items-center justify-between gap-2 rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-left text-xs text-amber-950 hover:border-amber-400 hover:bg-amber-100/60"
                          >
                            <span className="min-w-0 truncate font-medium">
                              {fmtNoShort(new Date(`${c.dayKey}T12:00:00`))}
                              {c.title ? ` · ${c.title}` : ""}
                            </span>
                            <span className="shrink-0 tabular-nums text-amber-800">
                              {c.timeLabel ||
                                t("work_order.plan_modal.conflicts_jump")}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <ReportAgreementWeekCalendar
                  employee={calendarEmployee}
                  weekStart={weekStart}
                  weekLabel={weekLabel}
                  onWeekChange={handleWeekChange}
                  onToday={handleGoTodayWeek}
                  appointment={null}
                  setAppointment={() => {}}
                  existing={existingEvents}
                  absenceDays={[...absenceDays]}
                  showWeekend
                  isLoading={eventsLoading || employeesLoading}
                  fixedDurationSlots={durationSlots}
                  draftBlockTitle={
                    title || workOrderLabel || projectActivityLabel || t("project.plan.title")
                  }
                  planSelectionMode
                  planSelection={activeSelection}
                  onPlanSelectionChange={onActiveSelectionChange}
                  topBar={calendarTopBar}
                  onRemovePlanDay={removeActivePlanDay}
                />

                <PlanScheduleModalFoot
                  planSelection={activeSelection}
                  weekStart={weekStart}
                  selectedEmployees={isDatesOnly ? [] : selectedEmployees}
                  assignmentCount={assignmentCount}
                  datesOnly={isDatesOnly}
                  hasConflict={!isDatesOnly && conflictItems.length > 0}
                  onClose={onClose}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  canSubmit={
                    canSubmitSchedule &&
                    (isDatesOnly || selectedEmployeeIds.length > 0)
                  }
                  submitLabel={
                    effectivePlan?.status === "draft"
                      ? t("project.plan.schedule_confirm")
                      : isSimpleActivityMode
                        ? t("project.plan.schedule_activity.save")
                        : t("Save")
                  }
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
